import { format, isBefore } from 'date-fns';
import { nl } from 'date-fns/locale';
import multiDownload from 'multi-download';
import { useRouter } from 'next/router';
import sanitizeHTML from 'sanitize-html';
import styled from 'styled-components';

import useUser from '../hooks/use-user';
import {
  useAttendActivityMutation,
  useUnattendActivityMutation,
} from '../lib/graphql';
import { FormatDate } from './date';
import { Spacer } from './spacer';
import { Button, ButtonSecondary } from './styled/button';
import { Card, CardImage } from './styled/card';
import { Tag } from './styled/tag';
import { Annotation, Heading, Text } from './styled/text';

const ContentItemContent = styled.div`
  padding: 1rem;
  border-bottom: 1px solid ${(props) => props.theme.stroke};
`;

const ContentItemContentHeader = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  justify-content: space-between;
`;

const ContentItemActions = styled.div`
  display: grid;
  grid-template-columns: auto auto;
  grid-gap: 1rem;
  place-content: start;
`;

export function ContentItem({
  activity,
  image,
  title,
  date,
  tag,
  summary,
  actions,
  children,
}) {
  const router = useRouter();
  const [{ fetching }, attendActivity] = useAttendActivityMutation();
  const [{ fetching: unattending }, unattendActivity] =
    useUnattendActivityMutation();
  const { user } = useUser();

  const isGoing =
    !!activity?.attendees?.find((attendee) => attendee.id === user.id) ?? false;
  const isExpired = isBefore(new Date(date), new Date());

  function trim(string, maxLength, ellips = '...') {
    return string.length > maxLength
      ? string.substring(0, maxLength - ellips.length) + ellips
      : string;
  }

  return (
    <Card>
      {image && <CardImage src={image} tag={tag} />}
      <ContentItemContent>
        <ContentItemContentHeader>
          <div>
            <Heading size="h3">{title}</Heading>
            <Annotation>
              {format(new Date(date), 'd MMMM yyyy', { locale: nl })}
            </Annotation>
          </div>
        </ContentItemContentHeader>
        <Spacer size={0.5} />
        <Text>
          {trim(
            sanitizeHTML(summary, {
              // Strip all html
              allowedTags: [],
              allowedAttributes: {},
            }),
            160
          )}
        </Text>
        <Spacer size={1} />
        <ContentItemActions>
          {!isGoing ? (
            <Button
              disabled={
                isExpired || activity.attendees.length >= activity.seats
              }
              title={
                isExpired ? 'Deze activiteit heeft al plaats gevonden' : ''
              }
              loading={fetching}
              onClick={() =>
                activity.attendees.length < activity.seats &&
                attendActivity({ id: activity.id })
              }
            >
              {isExpired
                ? 'Deelnemen'
                : activity.attendees.length >= activity.seats
                ? 'Deze activiteit zit vol'
                : 'Deelnemen'}
            </Button>
          ) : (
            <Button
              disabled={isExpired}
              title={
                isExpired ? 'Deze activiteit heeft al plaats gevonden' : ''
              }
              loading={unattending}
              onClick={() => unattendActivity({ id: activity.id })}
            >
              Niet meer deelnemen
            </Button>
          )}
          <ButtonSecondary
            onClick={() => router.push(`/activities/${activity.id}`)}
          >
            Meer informatie
          </ButtonSecondary>
          {actions && actions}
        </ContentItemActions>
      </ContentItemContent>
      {children}
    </Card>
  );
}

export function ResourceContentItem({ resource, children }) {
  return (
    <Card>
      <ContentItemContent>
        <ContentItemContentHeader>
          <div>
            <Heading size="h3">{resource.title}</Heading>
            <Annotation>
              <FormatDate date={resource.createdAt} format="d MMMM yyyy" />
            </Annotation>
          </div>
          <div>
            <Tag>Kennisitem</Tag>
          </div>
        </ContentItemContentHeader>
        <Spacer size={0.5} />
        <Text>{resource.description}</Text>
        <Spacer size={1} />
        <ContentItemActions>
          <Button
            onClick={() =>
              multiDownload(resource.media.map((media) => media.url))
            }
          >
            Download nu
          </Button>
        </ContentItemActions>
      </ContentItemContent>
      {children}
    </Card>
  );
}
